<template>
  <div class="dashboard-heading">
    <h1>{{ $t('Order') }} <span v-if="order">#{{order.id}}</span></h1>
  </div>
  <template v-if="!isLoading">
    <p v-if="!order" class="color-inactive">{{ $t('YouHaveNoOrdersYet') }}</p>
    <template v-if="order">
      <ul class="order-list">
      <li class="order">
        <div class="order-heading">
          <!-- Main Status Color -->
          <div v-if="order.status === 3"
               class="order-heading__decoration bg-color-success"></div>
          <div v-else-if="order.status === 1 && order.payment_status === 'SALE'"
               class="order-heading__decoration bg-color-blue"></div>
          <div v-else-if="order.status === 1"
               class="order-heading__decoration bg-color-blue"></div>
          <div v-else-if="order.status === 2"
               class="order-heading__decoration bg-color-blue-light"></div>
          <div v-else-if="order.status === 4"
               class="order-heading__decoration bg-color-alert"></div>
          <div v-else
               class="order-heading__decoration bg-color-inactive"></div>
          <!-- End Main Status Color -->
          <div class="order-heading__flex">
            <div class="order-heading__status">
              <p class="order-heading__label"> №&nbsp;{{ order.odoo_order_id ? order.odoo_order_id : order.id }} від
                <time > {{ order.date }}</time>
              </p>
              <!-- Main Status -->
              <p v-if="order.status === 3"
                 class="order-heading__value">
                {{ $t('Done') }}
              </p>
              <p v-else-if="order.status === 1 && order.payment_status === 'SALE'"
                 class="order-heading__value">
                {{ $t('Executed') }}
              </p>
              <p v-else-if="order.status === 1"
                 class="order-heading__value">
                {{ $t('AwaitingProcessing') }}
              </p>
              <p v-else-if="order.status === 2"
                 class="order-heading__value">
                {{ $t('Processed') }}
              </p>
              <p v-else-if="order.status === 4"
                 class="order-heading__value">
                {{ $t('Canceled') }}
              </p>
              <!-- End Main Status -->
            </div>
            <div class="order-heading__price show">
              <p class="order-heading__label">{{ $t('OrderAmount') }}</p>
              <p class="order-heading__value">{{ order.total}}<span class=" currency">₴</span></p>
            </div>
            <ul class="order-heading__goods show">
              <template v-for="(item, index) in order.items" v-bind:key="item.id">
                <li v-if="index <= 4" class="order-heading__goods-item">
                  <img loading="lazy"
                    :src="item.product_variation.image"
                    :alt="item.product_variation.title"
                    :title="item.product_variation.title">
                </li>
              </template>
            </ul>
          </div>
        </div>
        <div class="order-details active">
          <div class="order-details__summary">
            <div class="order-details__label">
              {{ $t('OrderInformation') }}
            </div>
            <ul class="order-details__summary-list">
              <li class="order-details__summary-route">
                <template v-if="order.shipping_type === 'self'">
                  {{ $t('Pickup') }}
                </template>
                <template v-else-if="order.shipping_type === 'taxi'">
                  {{ $t('TaxiDelivery') }}
                </template>
                <template v-else-if="order.shipping_type === 'nova_poshta'">
                  {{ $t('DeliveryWouldBeNovaPoshta') }}
                </template>
              </li>
              <li class="order-details__summary-recipient">
                <div class="order-details__summary-recipient__label">{{ $t('Recipient') }}</div>
                <div class="order-details__summary-recipient__details">
                  <p>{{ order.shipping_detail_last_first_name }}</p>
                  <p>{{ order.shipping_detail_phone }}</p>
                  <!-- Date -->
                  <template v-if="order.shipping_type === 'self'">
                    <div class="order-details__summary-recipient__details__label">{{ $t('Pickup') }}:</div>
                    <p>
                      {{ $t('with') }} {{ order.shipping_detail_interval_from }} {{ $t('on') }} {{ order.shipping_detail_interval_to }}
                      {{ order.shipping_detail_date }}
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'taxi'">
                    <div class="order-details__summary-recipient__details__label">{{ $t('Taxi') }}:</div>
                    <p>
                      {{ $t('with') }} {{ order.shipping_detail_interval_from }} {{ $t('on') }} {{ order.shipping_detail_interval_to }}
                      {{ order.shipping_detail_date }}
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'courier'">
                    <div class="order-details__summary-recipient__details__label">{{ $t('Courier') }}:</div>
                    <p>
                      {{ $t('with') }} {{ order.shipping_detail_interval_from }} {{ $t('on') }} {{ order.shipping_detail_interval_to }}
                      {{ order.shipping_detail_date }}
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'nova_poshta'">
                    <div class="order-details__summary-recipient__details__label">{{ $t('NovaPoshta') }}:</div>
                  </template>
                  <!-- End Date -->
                  <!-- Address -->
                  <template v-if="order.shipping_type === 'self'">
                    <p>
                      <template v-if="order.shipping_detail_self_point === 'in_stock_kyiv'">
                        {{ $t('in_stock_kyiv') }}
                      </template>
                      <template v-else-if="order.shipping_detail_self_point === 'in_stock_kyiv_2'">
                        {{ $t('in_stock_kyiv_2') }}
                      </template>
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'taxi'">
                    <p>
                      {{ order.shipping_detail_city }} {{ order.shipping_detail_address }}
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'nova_poshta'">
                    <p>
                      {{ order.shipping_detail_city }} {{ order.shipping_detail_address }}
                    </p>
                  </template>
                  <template v-if="order.shipping_type === 'courier'">
                    <p>
                      {{ order.shipping_detail_city }} {{ order.shipping_detail_address }}
                    </p>
                  </template>
                  <!-- End Address -->
                </div>
                <div class="order-details__summary-recipient__label">{{ $t('Payment') }}</div>
                <div class="order-details__summary-recipient__details">
                  <!-- Payment type -->
                  <p v-if="order.billing_detail_payment_type === 'on_delivery'">{{ $t('UponReceipt') }}</p>
                  <p v-if="order.billing_detail_payment_type === 'card'">{{ $t('ByCardOnTheWebsite') }}</p>
                  <p v-if="order.billing_detail_payment_type === 'privatbank'">{{ $t('PrivateBankCard') }}</p>
                  <!-- End Payment type -->
                  <!-- Payment status -->
                  <p v-if="order.payment_status === 'NOT_PAID'" class="color-alert">{{ $t('NotPaid') }}</p>
                  <p v-else-if="order.payment_status === 'SALE'" class="color-success">{{ $t('Paid') }}</p>
                  <p v-else-if="order.payment_status === 'REFUND'" class="color-default">{{ $t('PaymentWithdrawn') }}</p>
                  <p v-else-if="order.payment_status === 'CHARGEBACK'" class="color-inactive">{{ $t('Refunded') }}</p>
                  <!-- End Payment status -->
                </div>
              </li>
            </ul>
          </div>
          <div class="order-details__goods">
            <div class="order-details__label">
              {{ $t('Goods') }}
            </div>
            <ul class="order-details__items">
              <li v-for="item in order.items" v-bind:key="item.id">
                <div class="order-details__item">
                  <div class="order-details__item__title">
                    <div class="order-details__item__title__figure">
                      <img class="order-details__item__image"
                           :src="item.product_variation.image"
                           :alt="item.product_variation.title"
                           :title="item.product_variation.title"/>
                      <div class="order-details__item__title__figure__label">
                        <a :href="item.product_variation.get_absolute_url" target="_blank">
                          {{ item.product_variation.title }} ({{ item.product_variation.sku }})
                        </a>
                        <a class="order-details__item__title__figure__label__review"
                           :href="item.product_variation.get_absolute_url + '#reviews'" target="_blank">
                          {{ $t('LeaveAReview') }}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div class="order-details__item__options">
                    <div class="order-details__item__options__left">
                      <div class="order-details__label">{{ $t('Price') }}</div>
                      <div class="order-details__digit">{{ item.unit_price }} ₴</div>
                    </div>
                    <div class="order-details__item__options__center">
                      <div class="order-details__label">{{ $t('Number') }}</div>
                      <div class="order-details__digit">{{ item.quantity }}</div>
                    </div>
                    <div class="order-details__item__options__right">
                      <div class="order-details__label">{{ $t('Sum') }}</div>
                      <div class="order-details__digit">{{ mult(item.unit_price, item.quantity) }} ₴</div>
                    </div>
                  </div>
                </div>
                <hr class="merchandise-separator">
              </li>
            </ul>
            <ul class="order-details__payment">
              <li class="order-details__payment__row">
                <div class="order-details__payment__label">{{ $t('Payment') }}</div>
                <div class="order-details__payment__result">
                  <p v-if="order.billing_detail_payment_type === 'on_delivery'">{{ $t('UponReceipt') }}</p>
                  <p v-if="order.billing_detail_payment_type === 'card'">{{ $t('ByCardOnTheWebsite') }}</p>
                </div>
              </li>
              <li class="order-details__payment__row">
                <div class="order-details__payment__label">{{ $t('Status') }}</div>
                <div class="order-details__payment__result">
                  <!-- Payment status -->
                  <p v-if="order.payment_status === 'NOT_PAID'" class="color-alert">{{ $t('NotPaid') }}</p>
                  <p v-else-if="order.payment_status === 'SALE'" class="color-success">{{ $t('Paid') }}</p>
                  <p v-else-if="order.payment_status === 'REFUND'" class="color-default">{{ $t('PaymentWithdrawn') }}</p>
                  <p v-else-if="order.payment_status === 'CHARGEBACK'" class="color-inactive">{{ $t('Refunded') }}</p>
                  <!-- End Payment status -->
                </div>
              </li>
              <li v-if="order.discount_total" class="order-details__payment__row">
                <div class="order-details__payment__label">{{ $t('Discount') }}</div>
                <div class="order-details__payment__result">- {{ order.discount_total }} ₴</div>
              </li>
              <li v-if="order.shipping_type !== 'self'" class="order-details__payment__row">
                <div class="order-details__payment__label">{{ $t('Delivery') }}</div>
                <div class="order-details__payment__result color-inactive">({{ $t('SpecifiedByTheOperator') }})</div>
              </li>
              <li v-if="order.shipping_type !== 'self'" class="order-details__payment__row">
                <div class="order-details__payment__label">{{ $t('Delivery') }}</div>
                <div class="order-details__payment__result color-inactive">
                  <template v-if="order.shipping_type !== 'taxi' || order.shipping_total !== 0">
                    {{ order.shipping_total }}
                  </template>
                  <template v-else>
                    ({{ $t('SpecifiedByTheOperator') }})
                  </template>
                </div>
              </li>
              <template v-if="order.balance_withdrawal_total">
                <li class="order-details__payment__row">
                  <div class="order-details__payment__label">{{ $t('PaymentInGeneral') }}</div>
                  <div class="order-details__payment__result">{{ sum(order.balance_withdrawal_total, order.total) }} ₴</div>
                </li>
                <li class="order-details__payment__row">
                  <div class="order-details__payment__label">{{ $t('PaidWithBonuses') }}</div>
                  <div class="order-details__payment__result">{{ order.balance_withdrawal_total }} ₴</div>
                </li>
                <li class="order-details__payment__row">
                  <div class="order-details__payment__label">{{ $t('Total') }}</div>
                  <div class="order-details__payment__result">{{ order.total }} ₴</div>
                </li>
              </template>
              <template v-else>
                <li class="order-details__payment__row">
                  <div class="order-details__payment__label">{{ $t('Total') }}</div>
                  <div class="order-details__payment__result">{{ order.total }} ₴</div>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </li>
    </ul>
    </template>
  </template>
  <template v-if="isLoading">
    <dashboard-preloader/>
  </template>
</template>

<script>
import DashboardPreloader from "@/components/Preloader.vue"
import {actionTypes, getterTypes} from "@/store/modules/orders";
import {mapGetters} from "vuex";


export default {
  name: 'DashboardOrderSingle',
  components: {
    DashboardPreloader,
  },
  data(){
    return {
      logoChevronUp: require('@/assets/svg/chevron-compact-up.svg'),
      logoChevronDown: require('@/assets/svg/chevron-compact-down.svg'),
      logoLoad: require('@/assets/svg/arrow-clockwise.svg'),
      order: null
    }
  },
  mounted() {
    this.$store.dispatch(actionTypes.getOrder, this.orderId).then((data) => {
      this.order = data
    })
  },
  methods: {
    mult(value1, value2){
      return value1 * value2
    },
    sum(value1, value2){
      return value1 + value2
    }
  },
  computed: {
    ...mapGetters({
      isLoading: getterTypes.isLoading,
    }),
    currentUser() {
      return this.$store.state.auth.currentUser
    },
    orderId() {
      return this.$route.params.slug
    }
   }
}
</script>

<style lang="scss" scoped>
  @import "@/scss/variables.scss";

  p{
    margin-bottom: 0;
  }
  a{
    text-decoration: none;
  }
  ol, ul {
    list-style: none;
    padding: 0;
  }
  .pr-0{
    padding-right: 0;
  }
  .pl-0{
    padding-left: 0;
  }
  .dashboard-heading{
    margin-top: 20px;
  }
  .order-list{
    margin-bottom: 16px;
  }
  .order{
    padding: 16px;
    border: 1px solid #e9e9e9;
    border-radius: 4px;
    margin-bottom: 10px;
    @media only screen and (max-width: $max-width-mobile) {
      padding: 16px 10px;
    }
  }
  .order-heading{
    position: relative;
    padding-left: 24px;
    cursor: pointer;
    @media only screen and (max-width: $max-width-mobile) {
      padding-left: 16px;
    }
    &__decoration{
      position: absolute;
      left: 0;
      top: 0;
      display: block;
      height: 100%;
      width: 8px;
      border-radius: 50px;
      transition: all .2s ease-in-out;
    }
    &__flex{
      flex-direction: row;
      display: flex;
      align-items: center;
      height: 40px;
      min-height: 40px;
      padding-right: 24px;
      @media only screen and (max-width: $max-width-small) {
        padding-right: 5px;
      }
      @media only screen and (max-width: $max-width-mobile) {
        padding-right: 10px;
      }
    }
    &__status{
      width: 40%;
      margin-bottom: 0;
      @media only screen and (max-width: $max-width-small) {
        width: 100%;
      }
      @media only screen and (max-width: $max-width-mobile) and (min-width: $max-width-small + 1) {
        width: unset;
      }
    }
    &__label{
      font-size: 12px;
      color: $fi;
      @media only screen and (max-width: $max-width-mobile) {
        font-size: 10px;
      }
    }
    &__value{
      font-size: 14px;
    }
    &__price{
      width: 20%;
      margin-bottom: 0;
      @media only screen and (max-width: $max-width-mobile) {
        display: none;
      }
    }
    &__goods{
      justify-content: flex-end;
      width: 40%;
      margin-right: 24px;
      display: flex;
      flex-direction: row;
      @media only screen and (max-width: $max-width-mobile) {
        margin-right: 10px;
      }
    }
    &__goods-item{
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin-left: 5px;
      @media only screen and (max-width: $max-width-small) {
        display: none;
      }
      img{
        max-width: 100%;
        width: auto;
        max-height: 100%;
        height: auto;
      }
    }
    &__chevron{
      padding: 5px;
      cursor: pointer;
    }
  }
  .show-more{
    margin-top: 50px;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-top: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e9e9e9;
    cursor: pointer;
    color: $fd;
    font-weight: 900;
    img{
      width: 35px;
      height: 35px;
      margin-right: 10px;
      margin-left: 10px;
    }
  }
  .order-details{
    flex-direction: row;
    display: flex;
    padding-top: 16px;
    padding-left: 24px;
    @media only screen and (max-width: $max-width-tablet) {
      flex-direction: column;
    }
    &__label{
      font-size: 12px;
      color: $fi;
    }
    &__digit{
      font-size: 12px;
      color: $fd;
    }
    &__summary{
      width: 35%;
      margin-bottom: 0;
      padding-right: 24px;
      display: flex;
      flex-direction: column;
      @media only screen and (max-width: $max-width-tablet) {
        width: 100%;
      }
    }
    &__summary-route{
      font-size: 14px;
    }
    &__summary-recipient{
      &__label{
        padding-top: 10px;
        color: $fi;
        font-size: 12px;
      }
      &__details{
        font-size: 12px;
        color: $fd;
        &__label{
          padding-top: 10px;
          color: $fi;
          font-size: 12px;
        }
      }
    }
    &__goods{
      width: 65%;
      @media only screen and (max-width: $max-width-tablet) {
        width: 100%;
      }
    }
    &__items{
      padding-top: 5px;
    }
    &__item{
      display: flex;
      flex-direction: row;
      align-items: center;
      @media only screen and (max-width: $max-width-mobile) {
        flex-direction: column;
      }
      &__title{
        width: 50%;
        margin-bottom: 0;
        font-size: 12px;
        line-height: 17px;
        @media only screen and (max-width: $max-width-mobile) {
          width: 100%;
        }
        &__figure{
          display: flex;
          flex-direction: row;
          align-items: center;
          @media only screen and (max-width: $max-width-mobile) {
            padding-bottom: 10px;
          }
          &__label{
            color: $fi;
            font-size: 14px;
            word-break: break-word;
            display: flex;
            flex-flow: column;
            &__review{
              color: $fd !important;
            }
            a{
              color: $fi;
            }
          }
        }
      }
      &__image{
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 56px;
        height: 56px;
        margin-right: 16px;
        img{
          width: auto;
          max-width: 100%;
          height: auto;
          max-height: 100%;
        }
      }
      &__options{
        padding-left: 16px;
        width: 50%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        @media only screen and (max-width: $max-width-mobile) {
          width: 100%;
        }
        @media only screen and (max-width: $max-width-small) {
          flex-direction: column;
        }
        &__left{
          text-align: left;
          flex-grow: 0;
          .order-details__label{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
          .order-details__digit{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
        }
        &__center{
          flex-grow: 0;
          padding-left: 8px;
          padding-right: 8px;
          text-align: center;
          .order-details__label{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
          .order-details__digit{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
        }
        &__right{
          text-align: right;
          flex-grow: 1;
          .order-details__label{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
          .order-details__digit{
            @media only screen and (max-width: $max-width-small) {
              text-align: center;
            }
          }
        }
      }
    }
    &__payment{
      margin-bottom: 24px;
      line-height: 30px;
      &__row{
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
      }
      &__label{
        margin-bottom: 0;
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: 14px;
        color: #797878;
      }
    }
  }
  .inactive{
    display: none;
  }
  .active{
    display: flex;
  }
  .hide{
    opacity: 0;
  }
  .show{
    opacity: unset;
  }
  .color-alert{
    color: $danger;
  }
  .color-success{
    color: $success;
  }
  .color-default{
    color: $fd;
  }
  .color-inactive{
    color: $fi;
  }
  .bg-color-alert{
    background-color: $danger;
  }
  .bg-color-success{
    background-color: $success;
  }
  .bg-color-default{
    background-color: $fd;
  }
  .bg-color-blue{
    background-color: $bb-100;
  }
  .bg-color-blue-light{
    background-color: $bb-80;
  }
  .bg-color-inactive{
    background-color: $fi;
  }
  .merchandise-separator {
    margin-bottom: 16px;
    margin-top: 16px;
    border: 1px solid #e9e9e9;
  }
</style>
